import { CheckCircle as CheckIcon } from '@mui/icons-material';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useEffect, useState } from 'react';

import PricingConfigurator from './PricingConfigurator';
// Import types from local pricing types
import type { MarketplaceService } from '../../types/pricing';

interface ServiceVariantGridProps {
  service: MarketplaceService;
  selectedVariant?: string;
  selectedFrequency?: string;
  selectedTerm?: number | null;
  selectedMonthlyTotalCents?: number;
  monthlyOptionsTotalCents?: number;
  onVariantChange?: (variantKey: string) => void;
  onFrequencyChange?: (frequency: string) => void;
  className?: string;
}

/** Guest catalog-context payloads strip variant MRC/NRC. Never invent $0 from that blank. */
export const PRICE_UNAVAILABLE_LABEL = 'See availability';

type SelectedPricing = {
  price: string;
  suffix: string;
  variant?: any;
  frequency?: any;
};

function isPricingRecord(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function getVariantPricing(
  variant: any,
  selectedTerm?: number | null,
): Record<string, any> | null {
  if (!variant || typeof variant !== 'object') return null;

  let pricing: Record<string, any> | null = isPricingRecord(variant.pricing) ? variant.pricing : null;

  if (selectedTerm && Array.isArray(variant.term_overrides)) {
    const termOverride = variant.term_overrides.find(
      (override: any) => override?.term_months === selectedTerm,
    );
    if (termOverride && typeof termOverride === 'object') {
      pricing = {
        ...(pricing || {}),
        recurring_cents: termOverride.recurring_cents,
        nrc_cents: termOverride.nrc_cents,
        shipping_cents: termOverride.shipping_cents,
        other_cents: termOverride.other_cents,
      };
      if (termOverride.min_recurring_cents !== undefined) {
        pricing.min_recurring_cents = termOverride.min_recurring_cents;
      }
      if (termOverride.max_recurring_cents !== undefined) {
        pricing.max_recurring_cents = termOverride.max_recurring_cents;
      }
    }
  }

  return isPricingRecord(pricing) ? pricing : null;
}

function formatRecurringAmount(
  pricing: Record<string, any> | null,
  wholeDollars = false,
): string | null {
  if (!pricing) return null;

  const formatCents = (cents: number) =>
    wholeDollars ? (cents / 100).toFixed(0) : (cents / 100).toFixed(2);

  const min = Number(pricing.min_recurring_cents);
  const max = Number(pricing.max_recurring_cents);
  if (Number.isFinite(min) && min > 0 && Number.isFinite(max) && max > 0) {
    const minLabel = formatCents(min);
    const maxLabel = formatCents(max);
    return minLabel === maxLabel ? minLabel : `${minLabel}–${maxLabel}`;
  }

  const recurring = Number(pricing.recurring_cents);
  if (Number.isFinite(recurring) && recurring > 0) {
    return formatCents(recurring);
  }

  return null;
}

function getServiceLevelPriceLabel(service: MarketplaceService): SelectedPricing | null {
  const display = typeof (service as any)?.pricing?.display === 'string'
    ? String((service as any).pricing.display).trim()
    : '';
  if (display) {
    return { price: display, suffix: '' };
  }

  const minMonthly = Number((service as any)?.pricing?.min_monthly_cents);
  if (Number.isFinite(minMonthly) && minMonthly > 0) {
    return { price: (minMonthly / 100).toFixed(2), suffix: 'per month' };
  }

  return null;
}

function isPlainDollarAmount(value: string): boolean {
  return /^\d+(?:\.\d+)?(?:–\d+(?:\.\d+)?)?$/.test(value);
}

/**
 * FAANG-GRADE Service Variant Grid Component
 * Displays all service options in an elegant grid layout
 * Default option highlighted, price updates dynamically
 */
export const ServiceVariantGrid: React.FC<ServiceVariantGridProps> = ({
  service,
  selectedVariant,
  selectedFrequency,
  selectedTerm,
  selectedMonthlyTotalCents,
  monthlyOptionsTotalCents = 0,
  onVariantChange,
  onFrequencyChange,
  className,
}) => {
  const [internalSelectedVariant, setInternalSelectedVariant] = useState<string>(
    selectedVariant || service.default_variant_key || '',
  );
  const [internalSelectedFrequency, setInternalSelectedFrequency] = useState<string>(
    selectedFrequency || 'monthly',
  );

  // Initialize parent state with defaults on mount
  useEffect(() => {
    const defaultVariant = service.default_variant_key || '';
    const defaultFrequency = service.pricing_model === 'multi_frequency' ? 'monthly' : undefined;

    // Only call callbacks if they haven't been set yet (avoid overriding user selections)
    if (!selectedVariant && defaultVariant && onVariantChange) {
      onVariantChange(defaultVariant);
    }
    if (!selectedFrequency && defaultFrequency && onFrequencyChange) {
      onFrequencyChange(defaultFrequency);
    }
  }, [
    service.default_variant_key,
    service.pricing_model,
    selectedVariant,
    selectedFrequency,
    onVariantChange,
    onFrequencyChange,
  ]);

  const handleVariantSelect = (variantKey: string) => {
    setInternalSelectedVariant(variantKey);
    onVariantChange?.(variantKey);
  };

  const handleFrequencySelect = (frequency: string) => {
    setInternalSelectedFrequency(frequency);
    onFrequencyChange?.(frequency);
  };

  const getFrequencySuffix = (frequency: string): string => {
    switch (frequency) {
      case 'monthly':
        return 'per month';
      case 'quarterly':
        return 'per quarter';
      case 'annually':
        return 'per year';
      case 'one_time':
        return '';
      default:
        return '';
    }
  };

  const getSelectedPricing = (): SelectedPricing => {
    // PR-7/PR-8: when the API returned a presenter-resolved headline
    // (headline_monthly_cents), trust it unconditionally. The presenter is
    // the single source of truth for pricing; every branch below is legacy
    // fallback for services that have not yet cut over. This is what fixes
    // the Ooma AirDial '$0.00 per month' banner without special-casing the
    // pricing_model on the frontend.
    const headline = (service as any).headline_monthly_cents;
    if (typeof headline === 'number' && headline >= 0) {
      const price = (headline / 100).toFixed(2);
      return { price, suffix: 'per month' };
    }

    // Prefer variants whenever the payload contains a variants array, even
    // if the stale top-level pricing_model says 'fixed'. The API reports
    // the row's pricing_model from marketplace_services, but the real
    // pricing shape lives in variants. Without this fall-through a row
    // with pricing_model='fixed' + variants=[{min,max range}] reads as
    // $0.00 (see service 8ffdc90a-... flagged on 2026-04-17 for exactly
    // this reason).
    const hasVariantsArray =
      Array.isArray(service.variants?.variants) && service.variants.variants.length > 0;
    if ((service.pricing_model === 'variants' || hasVariantsArray) && service.variants) {
      const effectiveVariant =
        service.variants.variants.find((v) => v.key === internalSelectedVariant) || // variants-access-ok (Array.isArray guard above)
        service.variants.variants.find((v) => (v as any).is_default === true) || // variants-access-ok
        service.variants.variants[0]; // variants-access-ok
      if (effectiveVariant) {
        const pricing = getVariantPricing(effectiveVariant, selectedTerm);
        const formatted = formatRecurringAmount(pricing);
        if (formatted && pricing) {
          return {
            price: formatted,
            suffix: getFrequencySuffix(pricing.frequency),
            variant: { ...effectiveVariant, pricing },
          };
        }
      }
    } else if (service.pricing_model === 'multi_frequency' && service.frequencies) {
      const selectedFreqData = service.frequencies.find(
        (f) => f.frequency === internalSelectedFrequency,
      );
      if (selectedFreqData) {
        const price = (selectedFreqData.recurring_cents / 100).toFixed(2);
        const suffix = getFrequencySuffix(selectedFreqData.frequency);
        return { price, suffix, frequency: selectedFreqData };
      }
    } else if (service.pricing_model === 'varying' && service.default_pricing) {
      // Handle varying pricing (ranges)
      if (
        service.default_pricing.min_recurring_cents !== undefined &&
        service.default_pricing.max_recurring_cents !== undefined
      ) {
        const minPrice = (service.default_pricing.min_recurring_cents / 100).toFixed(2);
        const maxPrice = (service.default_pricing.max_recurring_cents / 100).toFixed(2);
        const suffix = getFrequencySuffix(service.default_pricing.frequency);
        const priceDisplay2 = minPrice === maxPrice ? minPrice : `${minPrice} - ${maxPrice}`;
        return { price: priceDisplay2, suffix };
      }
    } else if (service.default_pricing) {
      const price = (service.default_pricing.recurring_cents / 100).toFixed(2);
      const suffix = getFrequencySuffix(service.default_pricing.frequency);
      return { price, suffix };
    }
    const serviceLevel = getServiceLevelPriceLabel(service);
    if (serviceLevel) return serviceLevel;

    return { price: PRICE_UNAVAILABLE_LABEL, suffix: '' };
  };

  const pricing = getSelectedPricing();
  const selectedPriceDisplay = selectedMonthlyTotalCents !== undefined
    ? (selectedMonthlyTotalCents / 100).toFixed(2)
    : pricing.price;
  const selectedPriceHeading = isPlainDollarAmount(selectedPriceDisplay)
    ? `$${selectedPriceDisplay}`
    : selectedPriceDisplay;

  // PR-10 part 2: when the presenter emitted a configurator contract,
  // render the new <PricingConfigurator> panel (AWS-calculator-style
  // editable inputs + live breakdown + 'how this scales' disclosure) in
  // place of the static 'Selected Configuration' block. The rest of the
  // component (Choose Your Plan matrix, term selector, etc) continues to
  // render below when applicable. Services whose pricing model does not
  // emit a configurator fall through to the legacy static layout.
  const configurator = (service as any).configurator as
    | { fields: Array<any> }
    | undefined;
  const defaultConfiguration =
    (service as any).default_configuration || (service as any).defaultConfiguration;
  const howItScales = (service as any).how_it_scales || (service as any).howItScales;
  const hasLiveConfigurator =
    !!configurator &&
    Array.isArray(configurator.fields) &&
    configurator.fields.length > 0 &&
    !!defaultConfiguration;

  if (hasLiveConfigurator) {
    return (
      <Box className={className}>
        <PricingConfigurator
          serviceId={(service as any).id || (service as any).service_id}
          configurator={configurator as any}
          initialConfiguration={defaultConfiguration as any}
          howItScales={howItScales as any}
        />
        {/*
          The legacy Choose-Your-Plan variant matrix below this point is
          only relevant for services that mix a configurator with
          discrete variants (none today). We keep the old layout
          available for fall-through services by NOT rendering it here.
          When a future service needs both a stepper AND a variant
          matrix, refactor this branch to render both side-by-side; for
          now a single cleanly-scoped panel is the right default.
        */}
      </Box>
    );
  }

  return (
    <Box className={className}>
      {/* Current Selection Display */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <CheckIcon color="success" />
          Selected Configuration
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {selectedPriceHeading}{pricing.suffix ? ` ${pricing.suffix}` : ''}
          </Typography>

          {pricing.frequency && (
            <Chip
              label={`${pricing.frequency.frequency} billing`}
              color="secondary"
              variant="outlined"
            />
          )}
        </Box>

        {monthlyOptionsTotalCents > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Includes ${(monthlyOptionsTotalCents / 100).toFixed(2)}/mo in selected monthly options
          </Typography>
        )}

        {/* PR-10: presenter-supplied breakdown. Renders the 'how this
            number was built' line items for any service the backend has
            resolved a default_configuration for. The component is
            pricing-model-agnostic: it just consumes monthlyBreakdown /
            setupBreakdown arrays. When the presenter does not emit these
            (legacy services still on the old path) nothing renders here
            and the banner keeps its existing single-number appearance. */}
        {(() => {
          const dc = (service as any).default_configuration || (service as any).defaultConfiguration;
          if (!dc) return null;
          const monthly = Array.isArray(dc.monthlyBreakdown) ? dc.monthlyBreakdown : (dc.monthly_breakdown || []);
          const setup = Array.isArray(dc.setupBreakdown) ? dc.setupBreakdown : (dc.setup_breakdown || []);
          const monthlyTotal = dc.monthlyTotal || dc.monthly_total;
          const setupTotal = dc.setupTotal || dc.setup_total;
          if ((!monthly || monthly.length === 0) && (!setup || setup.length === 0)) return null;
          return (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1.5,
                bgcolor: (theme) => theme.palette.grey[50],
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {monthly && monthly.length > 0 && (
                <Box sx={{ mb: setup && setup.length > 0 ? 2 : 0 }}>
                  <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}
                  >
                    Monthly
                  </Typography>
                  {monthly.map((row: any, i: number) => (
                    <Box
                      key={`m-${i}`}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 2,
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {row.label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}>
                        {row.amount}
                      </Typography>
                    </Box>
                  ))}
                  {monthlyTotal && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 2,
                        pt: 1,
                        mt: 0.5,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Monthly total
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {monthlyTotal}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {setup && setup.length > 0 && (
                <Box>
                  <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}
                  >
                    One-time
                  </Typography>
                  {setup.map((row: any, i: number) => (
                    <Box
                      key={`s-${i}`}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 2,
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {row.label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}>
                        {row.amount}
                      </Typography>
                    </Box>
                  ))}
                  {setupTotal && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 2,
                        pt: 1,
                        mt: 0.5,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        One-time total
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {setupTotal}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          );
        })()}

        {/* Supplemental fees for selected option */}
        {pricing.variant?.pricing && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {pricing.variant.pricing.nrc_cents > 0 && (
              <Typography variant="body2" color="text.secondary">
                Setup: ${(pricing.variant.pricing.nrc_cents / 100).toFixed(2)}
              </Typography>
            )}
            {pricing.variant.pricing.shipping_cents > 0 && (
              <Typography variant="body2" color="text.secondary">
                Shipping: ${(pricing.variant.pricing.shipping_cents / 100).toFixed(2)}
              </Typography>
            )}
            {pricing.variant.pricing.other_cents > 0 && (
              <Typography variant="body2" color="text.secondary">
                Other: ${(pricing.variant.pricing.other_cents / 100).toFixed(2)}
              </Typography>
            )}
          </Box>
        )}

        {pricing.frequency && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {pricing.frequency.nrc_cents > 0 && (
              <Typography variant="body2" color="text.secondary">
                Setup: ${(pricing.frequency.nrc_cents / 100).toFixed(2)}
              </Typography>
            )}
            {pricing.frequency.shipping_cents > 0 && (
              <Typography variant="body2" color="text.secondary">
                Shipping: ${(pricing.frequency.shipping_cents / 100).toFixed(2)}
              </Typography>
            )}
            {pricing.frequency.other_cents > 0 && (
              <Typography variant="body2" color="text.secondary">
                Other: ${(pricing.frequency.other_cents / 100).toFixed(2)}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Variant Selection Grid - Only show if multiple variants to choose from.
          Guard on Array.isArray because pricing_model='variants' is not a
          reliable signal — some services store a non-standard inner shape. */}
      {service.pricing_model === 'variants' &&
        service.variants &&
        Array.isArray(service.variants.variants) &&
        service.variants.variants.length > 1 && ( // variants-access-ok (Array.isArray guard above)
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Choose Your Plan
            </Typography>

            {/* NxM Matrix Layout — renders whenever both attribute axes are present */}
            {service.variants.attribute1_options && service.variants.attribute2_options ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `auto repeat(${service.variants.attribute2_options.length}, 1fr)`,
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                {/* Top-left corner: attribute labels */}
                <Box sx={{ pb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {service.variants.attribute1_name}
                  </Typography>
                  <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mt: 0.25, mb: 0.25 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {service.variants.attribute2_name}
                  </Typography>
                </Box>

                {/* Column headers (attribute2) */}
                {service.variants.attribute2_options.map((attr2Value) => (
                  <Box key={attr2Value} sx={{ textAlign: 'center', pb: 0.5 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.primary">
                      {attr2Value}
                      {service.variants!.attribute2_name === 'Term' ? ' mo' : ''}
                    </Typography>
                  </Box>
                ))}

                {/* Data rows: one row per attribute1 value */}
                {service.variants.attribute1_options.map((attr1Value) => (
                  <React.Fragment key={attr1Value}>
                    {/* Row header (attribute1) */}
                    <Box sx={{ pr: 1.5, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight="bold" noWrap>
                        {attr1Value}
                      </Typography>
                    </Box>

                    {/* Cells */}
                    {service.variants!.attribute2_options!.map((attr2Value) => {
                      const variant = service.variants!.variants.find(
                        (v) =>
                          v.attribute1_value === attr1Value && v.attribute2_value === attr2Value,
                      );
                      if (!variant) return <Box key={attr2Value} />;

                      const isSelected = internalSelectedVariant === variant.key;
                      const variantPricing = getVariantPricing(variant, selectedTerm);
                      const formatted = formatRecurringAmount(variantPricing, true);
                      if (!formatted) return <Box key={attr2Value} />;
                      const price = formatted.includes('–')
                        ? `$${formatted.replace('–', '–$')}`
                        : `$${formatted}`;

                      return (
                        <Card
                          key={variant.key}
                          onClick={() => handleVariantSelect(variant.key)}
                          sx={{
                            cursor: 'pointer',
                            border: isSelected ? '2px solid' : '1px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            bgcolor: isSelected ? 'primary.light' : 'background.paper',
                            transition: 'all 0.15s ease-in-out',
                            '&:hover': { transform: 'translateY(-1px)', boxShadow: 2 },
                          }}
                        >
                          <CardContent sx={{ p: 1, textAlign: 'center', '&:last-child': { pb: 1 } }}>
                            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                              {price}
                            </Typography>
                            {formatted && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                /mo
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </React.Fragment>
                ))}
              </Box>
            ) : (
              /* Default 1xN Grid Layout */
              <Grid container spacing={2}>
                {service.variants.variants.map((variant) => { // variants-access-ok (Array.isArray + length guard above)
                  const isSelected = internalSelectedVariant === variant.key;
                  const variantPricing = getVariantPricing(variant, selectedTerm);
                  const formatted = formatRecurringAmount(variantPricing);
                  if (!formatted) return null;
                  const price = formatted;

                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 6 }} key={variant.key}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: isSelected ? '2px solid' : '1px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          bgcolor: isSelected ? 'primary.light' : 'background.paper',
                          position: 'relative',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 2,
                          },
                        }}
                        onClick={() => handleVariantSelect(variant.key)}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {variant.display_name}
                          </Typography>

                          <Typography
                            variant="h5"
                            sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}
                          >
                            {formatted ? `$${price}` : price}
                            {formatted && (
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ color: 'text.secondary', ml: 0.5 }}
                              >
                                {getFrequencySuffix(variantPricing?.frequency)}
                              </Typography>
                            )}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {variant.attribute1_value}
                            {variant.attribute2_value && ` • ${variant.attribute2_value}`}
                          </Typography>

                          {/* Pricing notes — escalation schedule, terms, etc. */}
                          {(variant as any).description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                mt: 1,
                                pt: 1,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                lineHeight: 1.45,
                                fontStyle: 'italic',
                              }}
                            >
                              {(variant as any).description}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

      {/* Frequency Selection Grid */}
      {service.pricing_model === 'multi_frequency' && service.frequencies && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Choose Billing Frequency
          </Typography>

          <Grid container spacing={2}>
            {service.frequencies.map((frequency) => {
              const isSelected = internalSelectedFrequency === frequency.frequency;

              return (
                <Grid size={{ xs: 12, sm: 6 }} key={frequency.frequency}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: isSelected ? '2px solid' : '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'primary.light' : 'background.paper',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => handleFrequencySelect(frequency.frequency)}
                  >
                    <CardContent sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6" gutterBottom sx={{ textTransform: 'capitalize' }}>
                        {frequency.frequency}
                      </Typography>

                      <Typography
                        variant="h4"
                        sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}
                      >
                        ${(frequency.recurring_cents / 100).toFixed(2)}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        per {frequency.frequency.slice(0, -2)} {/* Remove 'ly' */}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Varying Pricing Info */}
      {service.pricing_model === 'varying' && service.default_pricing && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Pricing Information
          </Typography>

          <Card sx={{ p: 3, bgcolor: 'background.paper' }}>
            <Typography variant="body1" gutterBottom>
              This service offers flexible pricing based on your specific requirements.
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Price Range:
              </Typography>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                {(() => {
                  const mn = ((service.default_pricing.min_recurring_cents || 0) / 100).toFixed(2);
                  const mx = ((service.default_pricing.max_recurring_cents || 0) / 100).toFixed(2);
                  return mn === mx ? mn : `${mn} - $${mx}`;
                })()} $
                {getFrequencySuffix(service.default_pricing.frequency)}
              </Typography>
            </Box>

            {service.default_pricing.nrc_cents > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Setup Fee:
                </Typography>
                <Typography variant="body1">
                  ${(service.default_pricing.nrc_cents / 100).toFixed(2)}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon color="warning" fontSize="small" />
                Contact provider for exact pricing quote based on your needs
              </Typography>
            </Box>
          </Card>
        </Box>
      )}
    </Box>
  );
};
